import React, { useState, useEffect, useContext } from 'react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  UserIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../App';
import ConfirmationModal from '../ConfirmationModal';
import AttributeLabel from '../AttributeLabel';
import { StatCard, StatGroup } from '../StatCard';
import { ModalShell, ModalActions, ModalSection } from './core';
import type { ModalCloseReason } from './core';

interface MemberAccess {
  mg_nr: number;
  name: string;
  roles: {
    vorstand: boolean;
    trainer: boolean;
    kassenpruefer: boolean;
  };
  access: {
    document_count: number;
    dog_document_count: number;
    total_count: number;
  };
  documents: Array<{
    id: number;
    name: string;
  }>;
}

interface DocumentDetail {
  id: number;
  name: string;
  created_at: string;
  size?: number;
  type?: string;
  tags: string[];
  creator?: string;
  access: {
    start_date: string | null;
    expiration_date: string | null;
    is_active: boolean;
  };
}

interface DogDocumentDetail {
  id: number;
  name: string;
  created_at: string;
  type?: string;
  dog_name: string;
  access: {
    start_date: string | null;
    expiration_date: string | null;
    is_active: boolean;
  };
}

interface MemberDetails {
  member: {
    mg_nr: number;
    name: string;
    email: string;
    roles: {
      vorstand: boolean;
      trainer: boolean;
      kassenpruefer: boolean;
    };
  };
  documents: DocumentDetail[];
  dog_documents: DogDocumentDetail[];
  stats: {
    active_documents: number;
    expired_documents: number;
    active_dog_documents: number;
    expired_dog_documents: number;
    total_documents: number;
  };
}

interface Stats {
  total_members: number;
  members_with_access: number;
  total_document_accesses: number;
  total_dog_document_accesses: number;
}

interface DocumentAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DocumentAccessModal: React.FC<DocumentAccessModalProps> = ({ isOpen, onClose }) => {
  const { BASE_URL } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<MemberAccess[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'vorstand' | 'trainer' | 'kassenpruefer'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'documents'>('name');

  // Grant Access Form State
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  // Revoke Access Form State
  const [showRevokeForm, setShowRevokeForm] = useState(false);
  const [selectedMembersRevoke, setSelectedMembersRevoke] = useState<number[]>([]);
  const [fromDateRevoke, setFromDateRevoke] = useState('');
  const [toDateRevoke, setToDateRevoke] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeResult, setRevokeResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  // Single Revoke Confirmation Modal State
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeData, setRevokeData] = useState<{
    documentId: number;
    memberId: number;
    documentName: string;
  } | null>(null);
  const [revokingDocument, setRevokingDocument] = useState(false);

  // Reencrypt Member Modal State
  const [showReencryptConfirm, setShowReencryptConfirm] = useState(false);
  const [reencryptData, setReencryptData] = useState<{
    memberId: number;
    memberName: string;
  } | null>(null);
  const [reencrypting, setReencrypting] = useState(false);

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchOverview();
    }
  }, [isOpen]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/access-overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Übersicht');
      }

      const data = await response.json();
      setOverview(data.overview);
      setStats(data.stats);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumentenrechte:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (memberId: number) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/access-details/${memberId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Details');
      }

      const data = await response.json();
      setMemberDetails(data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitglieds-Details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleMemberClick = (memberId: number) => {
    if (selectedMember === memberId) {
      setSelectedMember(null);
      setMemberDetails(null);
    } else {
      setSelectedMember(memberId);
      fetchMemberDetails(memberId);
    }
  };

  const handleGrantAccess = async () => {
    if (selectedMembers.length === 0) {
      setGrantResult({
        success: false,
        message: 'Bitte wählen Sie mindestens ein Mitglied aus.'
      });
      return;
    }

    if (!fromDate || !toDate) {
      setGrantResult({
        success: false,
        message: 'Bitte geben Sie einen Zeitraum an.'
      });
      return;
    }

    setGranting(true);
    setGrantResult(null);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/grant-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_ids: selectedMembers,
          from_date: fromDate,
          to_date: toDate,
          start_date: startDate || null,
          expiration_date: expirationDate || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setGrantResult({
          success: true,
          message: `Erfolgreich! ${data.granted} Rechte vergeben für ${data.total_documents} Dokumente an ${data.total_members} Mitglieder.`,
          details: data
        });

        // Reset form
        setSelectedMembers([]);
        setFromDate('');
        setToDate('');
        setStartDate('');
        setExpirationDate('');

        // Refresh overview
        fetchOverview();
      } else {
        setGrantResult({
          success: false,
          message: data.error || 'Fehler beim Vergeben der Rechte',
          details: data
        });
      }
    } catch (error) {
      console.error('Fehler beim Vergeben der Rechte:', error);
      setGrantResult({
        success: false,
        message: 'Netzwerkfehler beim Vergeben der Rechte'
      });
    } finally {
      setGranting(false);
    }
  };

  const toggleMemberSelection = (memberId: number) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAllMembers = () => {
    setSelectedMembers(filteredMembers.map(m => m.mg_nr));
  };

  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };

  // Revoke Access Functions
  const handleRevokeAccess = async () => {
    if (selectedMembersRevoke.length === 0) {
      setRevokeResult({
        success: false,
        message: 'Bitte wählen Sie mindestens ein Mitglied aus.'
      });
      return;
    }

    if (!fromDateRevoke || !toDateRevoke) {
      setRevokeResult({
        success: false,
        message: 'Bitte geben Sie einen Zeitraum an.'
      });
      return;
    }

    setRevoking(true);
    setRevokeResult(null);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/revoke-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_ids: selectedMembersRevoke,
          from_date: fromDateRevoke,
          to_date: toDateRevoke
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRevokeResult({
          success: true,
          message: `Erfolgreich! ${data.revoked} Rechte entzogen für ${data.total_documents} Dokumente von ${data.total_members} Mitgliedern.`,
          details: data
        });

        // Reset form
        setSelectedMembersRevoke([]);
        setFromDateRevoke('');
        setToDateRevoke('');

        // Refresh overview
        fetchOverview();
      } else {
        setRevokeResult({
          success: false,
          message: data.error || 'Fehler beim Entziehen der Rechte',
          details: data
        });
      }
    } catch (error) {
      console.error('Fehler beim Entziehen der Rechte:', error);
      setRevokeResult({
        success: false,
        message: 'Netzwerkfehler beim Entziehen der Rechte'
      });
    } finally {
      setRevoking(false);
    }
  };

  const toggleMemberSelectionRevoke = (memberId: number) => {
    setSelectedMembersRevoke(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAllMembersRevoke = () => {
    setSelectedMembersRevoke(filteredMembers.map(m => m.mg_nr));
  };

  const deselectAllMembersRevoke = () => {
    setSelectedMembersRevoke([]);
  };

  // Einzelnes Dokumentenrecht entziehen - Modal öffnen
  const handleRevokeSingleAccess = (documentId: number, memberId: number, documentName: string) => {
    setRevokeData({ documentId, memberId, documentName });
    setShowRevokeConfirm(true);
  };

  // Bestätigung des Rechte-Entzugs
  const confirmRevokeSingleAccess = async () => {
    if (!revokeData) return;

    setRevokingDocument(true);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/revoke-single-access`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: revokeData.documentId,
          member_id: revokeData.memberId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh member details and overview
        fetchMemberDetails(revokeData.memberId);
        fetchOverview();
        setShowRevokeConfirm(false);
        setRevokeData(null);
      } else {
        alert(data.error || 'Fehler beim Entziehen des Rechts');
      }
    } catch (error) {
      console.error('Fehler beim Entziehen des Rechts:', error);
      alert('Netzwerkfehler beim Entziehen des Rechts');
    } finally {
      setRevokingDocument(false);
    }
  };

  // Umschlüsselung für Mitglied - Modal öffnen
  const handleReencryptMember = (memberId: number, memberName: string) => {
    setReencryptData({ memberId, memberName });
    setShowReencryptConfirm(true);
  };

  // Bestätigung der Umschlüsselung
  const confirmReencryptMember = async () => {
    if (!reencryptData) return;

    setReencrypting(true);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/reencrypt-member`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_id: reencryptData.memberId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh member details and overview
        if (selectedMember === reencryptData.memberId) {
          fetchMemberDetails(reencryptData.memberId);
        }
        fetchOverview();
        setShowReencryptConfirm(false);
        setReencryptData(null);

        // Show success message
        alert(`Erfolgreich! ${data.reencrypted} von ${data.total_documents} Dokumenten wurden umgeschlüsselt für ${data.member_name}.` +
          (data.errors > 0 ? `\n\n${data.errors} Fehler sind aufgetreten.` : ''));
      } else {
        alert(data.error || 'Fehler bei der Umschlüsselung');
      }
    } catch (error) {
      console.error('Fehler bei der Umschlüsselung:', error);
      alert('Netzwerkfehler bei der Umschlüsselung');
    } finally {
      setReencrypting(false);
    }
  };

  const getRoleBadges = (roles: MemberAccess['roles']) => {
    const badges = [];
    if (roles.vorstand) {
      badges.push(
        <span key="vorstand" className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 border border-primary-200 dark:border-primary-800">
          Vorstand
        </span>
      );
    }
    if (roles.trainer) {
      badges.push(
        <span key="trainer" className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold bg-info-100 dark:bg-info-900/30 text-info-800 dark:text-info-200 border border-info-200 dark:border-info-800">
          Trainer
        </span>
      );
    }
    if (roles.kassenpruefer) {
      badges.push(
        <span key="kassenpruefer" className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-200 border border-success-200 dark:border-success-800">
          Kassenprüfer
        </span>
      );
    }
    return badges;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatSize = (bytes: number | undefined) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter and sort members
  const filteredMembers = overview
    .filter(member => {
      // Search filter
      if (searchTerm && !member.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      // Role filter
      if (filterRole !== 'all') {
        if (filterRole === 'vorstand' && !member.roles.vorstand) return false;
        if (filterRole === 'trainer' && !member.roles.trainer) return false;
        if (filterRole === 'kassenpruefer' && !member.roles.kassenpruefer) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        return b.access.total_count - a.access.total_count;
      }
    });

  return (
    <>
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        variant="detail"
        size="md"
        icon={ShieldCheckIcon}
        accent="brand"
        closeOnBackdrop={false}
        title="Dokumentenrechte-Verwaltung"
        subtitle="Übersicht über Zugriffsrechte aller Mitglieder"
        footer={<ModalActions secondary={{ label: 'Schließen', onClick: () => onClose() }} />}
      >
        <div className="space-y-5">
        {/* Statistics Bar */}
        {stats && (
          <ModalSection>
            <StatGroup columns={4}>
              <StatCard icon={UserIcon} tone="info" label="Mitglieder" value={stats.total_members} />
              <StatCard icon={CheckCircleIcon} tone="success" label="Mit Zugriff" value={stats.members_with_access} />
              <StatCard icon={DocumentTextIcon} tone="primary" label="Dokumente" value={stats.total_document_accesses} />
              <StatCard icon={ChartBarIcon} tone="warning" label="Hundedokumente" value={stats.total_dog_document_accesses} />
            </StatGroup>
          </ModalSection>
        )}

        {/* Filters */}
        <ModalSection>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Mitglied suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-base text-gray-900 dark:text-white"
              />
            </div>

            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-base text-gray-900 dark:text-white"
            >
              <option value="all">Alle Rollen</option>
              <option value="vorstand">Vorstand</option>
              <option value="trainer">Trainer</option>
              <option value="kassenpruefer">Kassenprüfer</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-base text-gray-900 dark:text-white"
            >
              <option value="name">Nach Name</option>
              <option value="documents">Nach Dokumenten</option>
            </select>

            {/* Grant Access Button */}
            <button
              onClick={() => {
                setShowGrantForm(!showGrantForm);
                if (!showGrantForm) setShowRevokeForm(false);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-colors text-base font-semibold whitespace-nowrap ${
                showGrantForm
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-success-600 text-white hover:bg-success-700'
              }`}
            >
              <PlusIcon className="h-5 w-5" />
              Rechte vergeben
            </button>

            {/* Revoke Access Button */}
            <button
              onClick={() => {
                setShowRevokeForm(!showRevokeForm);
                if (!showRevokeForm) setShowGrantForm(false);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-colors text-base font-semibold whitespace-nowrap ${
                showRevokeForm
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-error-600 text-white hover:bg-error-700'
              }`}
            >
              <XMarkIcon className="h-5 w-5" />
              Rechte entziehen
            </button>
          </div>
        </ModalSection>

        {/* Grant Access Form */}
        {showGrantForm && (
          <ModalSection>
          <div className="bg-info-50 dark:bg-info-900/20 rounded-xl border border-info-200 dark:border-info-800 px-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-info-900 dark:text-info-100 flex items-center gap-2">
                  <ShieldCheckIcon className="h-5 w-5" />
                  Dokumentenrechte vergeben
                </h4>
                <button
                  onClick={() => {
                    setShowGrantForm(false);
                    setGrantResult(null);
                  }}
                  className="text-info-600 dark:text-info-400 hover:text-info-800 dark:hover:text-info-200"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-info-700 dark:text-info-300">
                Vergeben Sie Rechte auf alle Dokumente, die in einem bestimmten Zeitraum erstellt wurden.
              </p>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <AttributeLabel iconKey="date" className="mb-1" textClassName="text-sm font-medium text-info-900 dark:text-info-100">
                    Dokumente von (erstellt am)
                  </AttributeLabel>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-info-300 dark:border-info-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <AttributeLabel iconKey="date" className="mb-1" textClassName="text-sm font-medium text-info-900 dark:text-info-100">
                    Dokumente bis (erstellt am)
                  </AttributeLabel>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-info-300 dark:border-info-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Access Period (Optional) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <AttributeLabel iconKey="date" className="mb-1" textClassName="text-sm font-medium text-info-900 dark:text-info-100">
                    Zugriff ab (optional)
                  </AttributeLabel>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-info-300 dark:border-info-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <AttributeLabel iconKey="date" className="mb-1" textClassName="text-sm font-medium text-info-900 dark:text-info-100">
                    Zugriff bis (optional)
                  </AttributeLabel>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-info-300 dark:border-info-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Member Selection Info */}
              <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-info-200 dark:border-info-800">
                <div className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedMembers.length} Mitglied{selectedMembers.length !== 1 ? 'er' : ''} ausgewählt
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllMembers}
                    className="text-xs text-info-600 dark:text-info-400 hover:underline"
                  >
                    Alle auswählen
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={deselectAllMembers}
                    className="text-xs text-info-600 dark:text-info-400 hover:underline"
                  >
                    Auswahl aufheben
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  onClick={handleGrantAccess}
                  disabled={granting || selectedMembers.length === 0 || !fromDate || !toDate}
                  className="flex-1 px-4 py-2 bg-success-600 hover:bg-success-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium text-sm disabled:cursor-not-allowed"
                >
                  {granting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Rechte werden vergeben...
                    </span>
                  ) : (
                    'Rechte vergeben'
                  )}
                </button>
              </div>

              {/* Result Message */}
              {grantResult && (
                <div className={`p-3 rounded-lg border ${
                  grantResult.success
                    ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                    : 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800'
                }`}>
                  <p className={`text-sm font-medium ${
                    grantResult.success
                      ? 'text-success-900 dark:text-success-100'
                      : 'text-error-900 dark:text-error-100'
                  }`}>
                    {grantResult.message}
                  </p>
                  {grantResult.details?.errors > 0 && (
                    <p className="text-xs text-error-700 dark:text-error-300 mt-1">
                      {grantResult.details.errors} Fehler aufgetreten
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          </ModalSection>
        )}

        {/* Revoke Access Form */}
        {showRevokeForm && (
          <ModalSection>
          <div className="bg-error-50 dark:bg-error-900/20 rounded-xl border border-error-200 dark:border-error-800 px-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-error-900 dark:text-error-100 flex items-center gap-2">
                  <XCircleIcon className="h-5 w-5" />
                  Dokumentenrechte entziehen
                </h4>
                <button
                  onClick={() => {
                    setShowRevokeForm(false);
                    setRevokeResult(null);
                  }}
                  className="text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-200"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-error-700 dark:text-error-300">
                Entziehen Sie Rechte von allen Dokumenten, die in einem bestimmten Zeitraum erstellt wurden.
              </p>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <AttributeLabel iconKey="date" className="mb-1" textClassName="text-sm font-medium text-error-900 dark:text-error-100">
                    Dokumente von (erstellt am)
                  </AttributeLabel>
                  <input
                    type="date"
                    value={fromDateRevoke}
                    onChange={(e) => setFromDateRevoke(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-error-300 dark:border-error-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <AttributeLabel iconKey="date" className="mb-1" textClassName="text-sm font-medium text-error-900 dark:text-error-100">
                    Dokumente bis (erstellt am)
                  </AttributeLabel>
                  <input
                    type="date"
                    value={toDateRevoke}
                    onChange={(e) => setToDateRevoke(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-error-300 dark:border-error-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Member Selection Info */}
              <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-error-200 dark:border-error-800">
                <div className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-error-600 dark:text-error-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedMembersRevoke.length} Mitglied{selectedMembersRevoke.length !== 1 ? 'er' : ''} ausgewählt
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllMembersRevoke}
                    className="text-xs text-error-600 dark:text-error-400 hover:underline"
                  >
                    Alle auswählen
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={deselectAllMembersRevoke}
                    className="text-xs text-error-600 dark:text-error-400 hover:underline"
                  >
                    Auswahl aufheben
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  onClick={handleRevokeAccess}
                  disabled={revoking || selectedMembersRevoke.length === 0 || !fromDateRevoke || !toDateRevoke}
                  className="flex-1 px-4 py-2 bg-error-600 hover:bg-error-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium text-sm disabled:cursor-not-allowed"
                >
                  {revoking ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Rechte werden entzogen...
                    </span>
                  ) : (
                    'Rechte entziehen'
                  )}
                </button>
              </div>

              {/* Result Message */}
              {revokeResult && (
                <div className={`p-3 rounded-lg border ${
                  revokeResult.success
                    ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                    : 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800'
                }`}>
                  <p className={`text-sm font-medium ${
                    revokeResult.success
                      ? 'text-success-900 dark:text-success-100'
                      : 'text-error-900 dark:text-error-100'
                  }`}>
                    {revokeResult.message}
                  </p>
                  {revokeResult.details?.errors > 0 && (
                    <p className="text-xs text-error-700 dark:text-error-300 mt-1">
                      {revokeResult.details.errors} Fehler aufgetreten
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          </ModalSection>
        )}

        {/* Content */}
        <ModalSection>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
                  <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    <div className="flex gap-1.5">
                      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                    </div>
                  </div>
                  <div className="flex items-center gap-8">
                    <div className="space-y-1 text-right">
                      <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      <div className="h-5 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    </div>
                    <div className="space-y-1 text-right">
                      <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      <div className="h-5 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((member) => (
                <div
                  key={member.mg_nr}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Member Row */}
                  <div className="flex items-center">
                    {/* Checkbox for selection */}
                    {(showGrantForm || showRevokeForm) && (
                      <div className="px-4 py-3 flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            showGrantForm
                              ? selectedMembers.includes(member.mg_nr)
                              : selectedMembersRevoke.includes(member.mg_nr)
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (showGrantForm) {
                              toggleMemberSelection(member.mg_nr);
                            } else {
                              toggleMemberSelectionRevoke(member.mg_nr);
                            }
                          }}
                          className={`h-4 w-4 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 ${
                            showGrantForm
                              ? 'text-success-600'
                              : 'text-error-600'
                          }`}
                        />
                      </div>
                    )}

                    <button
                      onClick={() => handleMemberClick(member.mg_nr)}
                      className="flex-1 px-5 py-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <UserIcon className="h-6 w-6 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <p className="font-semibold text-base text-gray-900 dark:text-white">{member.name}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {getRoleBadges(member.roles)}
                          </div>
                        </div>
                        <div className="flex items-center gap-8">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Dokumente</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{member.access.document_count}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Hundedok.</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{member.access.dog_document_count}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Gesamt</p>
                            <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{member.access.total_count}</p>
                          </div>
                        </div>
                      </div>

                      {/* Reencrypt Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReencryptMember(member.mg_nr, member.name);
                        }}
                        className="p-2 hover:bg-warning-100 dark:hover:bg-warning-900/30 rounded-lg transition-colors group ml-2"
                        title="Schlüssel umschließen (nach Passwort-Reset)"
                      >
                        <ArrowPathIcon className="h-5 w-5 text-gray-400 group-hover:text-warning-600 dark:group-hover:text-warning-400" />
                      </button>

                      {selectedMember === member.mg_nr ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
                      )}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {selectedMember === member.mg_nr && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
                      {loadingDetails ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-200 border-t-primary-600"></div>
                        </div>
                      ) : memberDetails ? (
                        <div className="space-y-4">
                          {/* Member Info */}
                          <div className="bg-info-50 dark:bg-info-900/20 rounded-lg p-5 border border-info-200 dark:border-info-800">
                            <div className="flex items-center gap-3 mb-4">
                              <UserIcon className="h-6 w-6 text-info-600 dark:text-info-400" />
                              <h4 className="font-bold text-lg text-info-900 dark:text-info-100">Mitgliedsinformationen</h4>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-base">
                              <div>
                                <AttributeLabel iconKey="name" textClassName="text-info-700 dark:text-info-300 font-semibold">Name:</AttributeLabel>
                                <span className="ml-2 text-info-900 dark:text-info-100">{memberDetails.member.name}</span>
                              </div>
                              <div>
                                <AttributeLabel iconKey="email" textClassName="text-info-700 dark:text-info-300 font-semibold">Email:</AttributeLabel>
                                <span className="ml-2 text-info-900 dark:text-info-100">{memberDetails.member.email}</span>
                              </div>
                            </div>
                          </div>

                          {/* Statistics */}
                          <StatGroup columns={5}>
                            <StatCard icon={CheckCircleIcon} tone="success" valueTone="success" label="Aktive Dokumente" value={memberDetails.stats.active_documents} />
                            <StatCard icon={XCircleIcon} tone="error" valueTone="error" label="Abgelaufen" value={memberDetails.stats.expired_documents} />
                            <StatCard icon={CheckCircleIcon} tone="info" valueTone="info" label="Aktive Hundedok." value={memberDetails.stats.active_dog_documents} />
                            <StatCard icon={XCircleIcon} tone="warning" valueTone="warning" label="Abgel. Hundedok." value={memberDetails.stats.expired_dog_documents} />
                            <StatCard icon={DocumentTextIcon} tone="primary" valueTone="primary" label="Gesamt" value={memberDetails.stats.total_documents} />
                          </StatGroup>

                          {/* Documents List */}
                          {memberDetails.documents.length > 0 && (
                            <div>
                              <h5 className="font-semibold text-base text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <DocumentTextIcon className="h-5 w-5" />
                                Dokumente ({memberDetails.documents.length})
                              </h5>
                              <div className="space-y-3 max-h-72 overflow-y-auto">
                                {memberDetails.documents.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className={`p-4 rounded-lg border ${
                                      doc.access.is_active
                                        ? 'bg-success-50 dark:bg-success-900/10 border-success-200 dark:border-success-800'
                                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-base text-gray-900 dark:text-white truncate">{doc.name}</p>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                          {doc.tags.map((tag, idx) => (
                                            <span key={idx} className="text-sm px-2.5 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md font-medium">
                                              {tag}
                                            </span>
                                          ))}
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                          {doc.creator} • {formatSize(doc.size)} • {formatDate(doc.created_at)}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-3 flex-shrink-0">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRevokeSingleAccess(doc.id, memberDetails.member.mg_nr, doc.name);
                                          }}
                                          className="p-2 hover:bg-error-100 dark:hover:bg-error-900/30 rounded-lg transition-colors group"
                                          title="Recht entziehen"
                                        >
                                          <TrashIcon className="h-5 w-5 text-gray-400 group-hover:text-error-600 dark:group-hover:text-error-400" />
                                        </button>
                                        {doc.access.is_active ? (
                                          <CheckCircleIcon className="h-6 w-6 text-success-600 dark:text-success-400" />
                                        ) : (
                                          <XCircleIcon className="h-6 w-6 text-error-600 dark:text-error-400" />
                                        )}
                                      </div>
                                    </div>
                                    {(doc.access.start_date || doc.access.expiration_date) && (
                                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                        <ClockIcon className="h-4 w-4" />
                                        {doc.access.start_date && <span>Ab: {formatDate(doc.access.start_date)}</span>}
                                        {doc.access.expiration_date && <span>Bis: {formatDate(doc.access.expiration_date)}</span>}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Dog Documents List */}
                          {memberDetails.dog_documents.length > 0 && (
                            <div>
                              <h5 className="font-semibold text-base text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <DocumentTextIcon className="h-5 w-5" />
                                Hundedokumente ({memberDetails.dog_documents.length})
                              </h5>
                              <div className="space-y-3 max-h-72 overflow-y-auto">
                                {memberDetails.dog_documents.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className={`p-4 rounded-lg border ${
                                      doc.access.is_active
                                        ? 'bg-info-50 dark:bg-info-900/10 border-info-200 dark:border-info-800'
                                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-base text-gray-900 dark:text-white truncate">{doc.name}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                          Hund: {doc.dog_name} • {doc.type} • {formatDate(doc.created_at)}
                                        </p>
                                      </div>
                                      <div className="flex-shrink-0">
                                        {doc.access.is_active ? (
                                          <CheckCircleIcon className="h-6 w-6 text-info-600 dark:text-info-400" />
                                        ) : (
                                          <XCircleIcon className="h-6 w-6 text-error-600 dark:text-error-400" />
                                        )}
                                      </div>
                                    </div>
                                    {(doc.access.start_date || doc.access.expiration_date) && (
                                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                        <ClockIcon className="h-4 w-4" />
                                        {doc.access.start_date && <span>Ab: {formatDate(doc.access.start_date)}</span>}
                                        {doc.access.expiration_date && <span>Bis: {formatDate(doc.access.expiration_date)}</span>}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {memberDetails.documents.length === 0 && memberDetails.dog_documents.length === 0 && (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                              <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                              <p>Keine Dokumente vorhanden</p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}

              {filteredMembers.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <UserIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>Keine Mitglieder gefunden</p>
                </div>
              )}
            </div>
          )}
        </ModalSection>
        </div>
      </ModalShell>

      {/* Confirmation Modal für Rechte-Entzug */}
      <ConfirmationModal
        visible={showRevokeConfirm}
        title="Recht entziehen"
        message={`Möchten Sie wirklich das Zugriffsrecht für "${revokeData?.documentName}" entziehen?\n\nDas Mitglied wird keinen Zugriff mehr auf dieses Dokument haben.`}
        type="warning"
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => {
              setShowRevokeConfirm(false);
              setRevokeData(null);
            }
          },
          confirm: {
            text: 'Recht entziehen',
            onPress: confirmRevokeSingleAccess,
            loading: revokingDocument
          }
        }}
      />

      {/* Confirmation Modal für Umschlüsselung */}
      <ConfirmationModal
        visible={showReencryptConfirm}
        title="Schlüssel umschließen"
        message={`Möchten Sie wirklich alle Dokumentenschlüssel für "${reencryptData?.memberName}" umschließen?\n\nDieser Vorgang wird verwendet, wenn das Mitglied sein Passwort vergessen hat und neue Schlüssel benötigt.\n\nAlle existierenden Schlüssel werden gelöscht und mit Ihrem Private Key neu verschlüsselt.`}
        type="warning"
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => {
              setShowReencryptConfirm(false);
              setReencryptData(null);
            }
          },
          confirm: {
            text: 'Umschließen',
            onPress: confirmReencryptMember,
            loading: reencrypting
          }
        }}
      />
    </>
  );
};

export default DocumentAccessModal;
